import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterSidebarProps {
  categories: FilterOption[];
  manufacturers: FilterOption[];
  certifications: FilterOption[];
  priceRanges: FilterOption[];
  availabilityOptions: FilterOption[];
  selectedFilters: {
    categories: string[];
    manufacturers: string[];
    certifications: string[];
    priceRanges: string[];
    availability: string[];
  };
  onFilterChange: (filterType: string, value: string) => void;
  onClearAll: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

const FilterSidebar = ({
  categories,
  manufacturers,
  certifications,
  priceRanges,
  availabilityOptions,
  selectedFilters,
  onFilterChange,
  onClearAll,
  isMobileOpen,
  onMobileClose,
}: FilterSidebarProps) => {
  const FilterSection = ({
    title,
    options,
    filterType,
    selectedValues,
  }: {
    title: string;
    options: FilterOption[];
    filterType: string;
    selectedValues: string[];
  }) => {
    const [isExpanded, setIsExpanded] = React.useState(true);

    return (
      <div className="border-b border-border pb-4 mb-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between mb-3"
        >
          <h3 className="text-sm font-headline font-semibold text-foreground">{title}</h3>
          <Icon
            name="ChevronDownIcon"
            size={18}
            variant="outline"
            className={`text-muted-foreground transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>
        {isExpanded && (
          <div className="space-y-2">
            {options.map((option) => (
              <label
                key={option.id}
                className="flex items-center space-x-2 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.id)}
                  onChange={() => onFilterChange(filterType, option.id)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                />
                <span className="text-sm text-muted-foreground group-hover:text-foreground flex-1">
                  {option.label}
                </span>
                <span className="text-xs text-muted-foreground">({option.count})</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-headline font-bold text-foreground">Filters</h2>
        <button
          onClick={onClearAll}
          className="text-sm text-primary hover:text-primary/80 font-semibold"
        >
          Clear All
        </button>
      </div>

      <FilterSection
        title="Category"
        options={categories}
        filterType="categories"
        selectedValues={selectedFilters.categories}
      />

      <FilterSection
        title="Availability"
        options={availabilityOptions}
        filterType="availability"
        selectedValues={selectedFilters.availability}
      />

      <FilterSection
        title="Price Range"
        options={priceRanges}
        filterType="priceRanges"
        selectedValues={selectedFilters.priceRanges}
      />

      <FilterSection
        title="Manufacturer"
        options={manufacturers}
        filterType="manufacturers"
        selectedValues={selectedFilters.manufacturers}
      />

      <FilterSection
        title="Certifications"
        options={certifications}
        filterType="certifications"
        selectedValues={selectedFilters.certifications}
      />
    </>
  );

  return (
    <>
      <div className="hidden lg:block w-72 flex-shrink-0">
        <div className="bg-card border border-border rounded-lg p-6 sticky top-20">
          {sidebarContent}
        </div>
      </div>

      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
          <div className="fixed inset-y-0 left-0 w-80 bg-card shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-headline font-bold text-foreground">Filters</h2>
                <button
                  onClick={onMobileClose}
                  className="p-2 hover:bg-muted rounded-md transition-colors duration-200"
                >
                  <Icon name="XMarkIcon" size={24} variant="outline" />
                </button>
              </div>
              {sidebarContent}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterSidebar;